import { flushSync } from 'react-dom';
import { createContext,DependencyList,EffectCallback,RefObject,useContext,useEffect,useLayoutEffect,useMemo,useRef,useState,useSyncExternalStore } from 'react';


/** Shared content breakpoint; device type never determines a pane's layout. */
export const COMPACT_CONTENT_WIDTH = 768;
export function useContainerWidth(ref: RefObject<HTMLElement | null>) {
    const [width,setWidth] = useState<number | null>(null);
    useLayoutEffect(() => {
        const element=ref.current;
        if (!element) return;
        let measured=element.clientWidth;
        if(measured>0)setWidth(measured);
        let frame:number | undefined;
        const update=()=>{
            if(frame!==undefined || element.clientWidth<=0 || element.clientWidth===measured)return;
            // Never mutate an observed layout during ResizeObserver delivery.
            frame=requestAnimationFrame(()=>{
                frame=undefined;
                const next=element.clientWidth;
                if(next<=0 || next===measured)return;
                measured=next;
                // Read the latest width and commit before this animation frame paints.
                flushSync(()=>setWidth(next));
            });
        };
        const observer=typeof ResizeObserver==='undefined' ? undefined : new ResizeObserver(update);
        if(observer)observer.observe(element);
        else window.addEventListener('resize',update);
        return ()=>{
            observer?.disconnect();
            window.removeEventListener('resize',update);
            if(frame!==undefined)cancelAnimationFrame(frame);
        };
    },[ref]);
    return width;
}

/**
 * Browser detection utilities for handling platform-specific CSS issues
 */

export const detectBrowser = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  
  return {
    isWechatDesktop: userAgent.includes('micromessenger') && !userAgent.includes('mobile'),
    isWechatMobile: userAgent.includes('micromessenger') && userAgent.includes('mobile'),
    isWechat: userAgent.includes('micromessenger'),
    isIOS: /iphone|ipad|ipod/.test(userAgent) || (userAgent.includes('macintosh') && navigator.maxTouchPoints > 1),
    isMobile: /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent),
    isDesktop: !/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent)
  };
};

/**
 * Get safe CSS values based on browser capabilities
 */
export const getSafeCSS = () => {
  const browser = detectBrowser();
  
  return {
    // Height values - use vh for WeChat desktop, dvh for modern browsers
    fullHeight: browser.isWechatDesktop ? '100vh' : '100dvh',
    minFullHeight: browser.isWechatDesktop ? 'min-h-screen' : 'min-h-[100dvh]',
    
    // Safe area insets - only disable for WeChat desktop
    topSafeArea: browser.isWechatDesktop ? '0px' : 'env(safe-area-inset-top)',
    bottomSafeArea: browser.isWechatDesktop ? '0px' : 'env(safe-area-inset-bottom)',
    
    // Calc functions - preserve safe area for non-WeChat desktop
    getCalcHeight: (subtract: string) => {
      if (browser.isWechatDesktop) {
        return `calc(100vh - ${subtract})`;
      }
      // For iOS and other modern browsers, include safe area
      return `calc(100dvh - ${subtract} - env(safe-area-inset-top))`;
    },
    
    // CSS classes
    getHeightClass: (fallback: string) => 
      browser.isWechatDesktop ? fallback : 'min-h-[100dvh]'
  };
};

/**
 * Hook for reactive browser detection with memoization
 */

export const useBrowserDetection = () => {
  const { browser, safeCSS } = useMemo(() => {
    const detectedBrowser = detectBrowser();
    const cssValues = getSafeCSS();
    return { 
      browser: detectedBrowser, 
      safeCSS: cssValues 
    };
  }, []); // Empty dependency array since browser doesn't change during session
  
  return { browser, safeCSS };
};

const MOBILE_BREAKPOINT = 768; // Tailwind's 'md' breakpoint is 768px

export function useIsMobile(breakpoint: number = MOBILE_BREAKPOINT) {
    const [isMobile, setIsMobile] = useState<boolean>(() => typeof window !== 'undefined' && window.matchMedia(`(max-width: ${breakpoint - 1}px)`).matches);

    useEffect(() => {
        const mediaQuery = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
        
        // Initial check
        setIsMobile(mediaQuery.matches);

        const listener = (e: MediaQueryListEvent) => {
            setIsMobile(e.matches);
        };

        // Support for modern browsers and fallback for older ones
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', listener);
            return () => mediaQuery.removeEventListener('change', listener);
        } else if (mediaQuery.addListener) {
            // @ts-ignore
            mediaQuery.addListener(listener);
            // @ts-ignore
            return () => mediaQuery.removeListener(listener);
        }
    }, [breakpoint]);

    return isMobile;
}

/**
 * Initialize safe area CSS based on browser capabilities
 * This must be called early in the app lifecycle
 */

export const initSafeAreaCSS = () => {
  const browser = detectBrowser();
  
  // Only apply safe area insets for non-WeChat desktop browsers
  if (!browser.isWechatDesktop) {
    const style = document.createElement('style');
    style.textContent = `
      html, body {
        padding-top: env(safe-area-inset-top) !important;
        padding-bottom: env(safe-area-inset-bottom) !important;
      }
    `;
    document.head.appendChild(style);
  }
  
  // For WeChat desktop, ensure no safe area padding
  if (browser.isWechatDesktop) {
    const style = document.createElement('style');
    style.textContent = `
      html, body {
        padding-top: 0px !important;
        padding-bottom: 0px !important;
      }
    `;
    document.head.appendChild(style);
  }
};

/** Safari can ignore viewport limits for manual zoom; cancel native pinch gestures too. */
export function installPageZoomGuard() {
    const preventZoom = (event: Event) => { if (event.cancelable) event.preventDefault(); };
    const preventPinch = (event: TouchEvent) => { if (event.touches.length > 1) preventZoom(event); };
    const options = {passive: false};
    const gestures = ['gesturestart', 'gesturechange', 'gestureend'];
    gestures.forEach(name => document.addEventListener(name, preventZoom, options));
    document.addEventListener('touchmove', preventPinch, options);
    return () => {
        gestures.forEach(name => document.removeEventListener(name, preventZoom));
        document.removeEventListener('touchmove', preventPinch);
    };
}

/** Global listeners and timers belong to the visible workspace, not its lifetime. */
export function useActiveEffect(effect: EffectCallback, dependencies: DependencyList) {
    const active = useRouteActive();
    // Dependencies are supplied by the owner, exactly as with useEffect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => active ? effect() : undefined, [active, ...dependencies]);
}

/** Shared persistence/subscription primitive. Product data and permissions never live here. */
export function createPreference<T>(key:string, read:()=>T, write:(value:T)=>void) {
    let value:T; let initialized=false; let volatile=false;
    const listeners=new Set<()=>void>();
    const refresh=()=>{
        const next=read();
        if(!initialized || JSON.stringify(value)!==JSON.stringify(next)){value=next;initialized=true;}
    };
    const get=()=>{if(!initialized)refresh();return value;};
    const set=(next:T)=>{value=next;initialized=true;try{write(next);volatile=false;}catch{volatile=true;/* Session preference survives blocked storage. */}listeners.forEach(fn=>fn());};
    const subscribe=(listener:()=>void)=>{
        if(!volatile)refresh();listeners.add(listener);
        return ()=>{listeners.delete(listener);};
    };
    const onStorage=(event:StorageEvent)=>{if(event.key===key || event.key===null){volatile=false;refresh();listeners.forEach(fn=>fn());}};
    if(typeof window!=='undefined')window.addEventListener('storage',onStorage);
    return {get,set,subscribe,useValue:()=>useSyncExternalStore(subscribe,get,get)};
}

/**
 * Simple conditional class name merger
 * Using a simple implementation instead of clsx/tailwind-merge for now
 * to match existing behavior.
 */
export function cn(...inputs: (string | undefined | null | boolean)[]) {
    return inputs.filter(Boolean).join(' ')
}

/** Kept-alive pages retain drafts, but do not own background reads while hidden. */
export const RouteActivityContext = createContext(true);
export const useRouteActive = () => useContext(RouteActivityContext);
/** Opt-in for ERP retention; other products keep their existing resume policy. */
export const RetainedRouteContext = createContext(false);
export const useRetainedRoute = () => useContext(RetainedRouteContext);

/** Download a generated file, retaining its URL until the browser starts the download. */
export function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = filename; link.hidden = true;
    try { document.body.appendChild(link); link.click(); }
    finally { link.remove(); setTimeout(()=>URL.revokeObjectURL(url), 1000); }
}

export function downloadJson(filename:string, data:unknown) {
    downloadBlob(new Blob([JSON.stringify(data,null,2)],{type:'application/json'}),filename);
}

/** Mobile content gestures activate the existing tab buttons, preserving their state/guards. */
export function useSwipeTabs(root: RefObject<HTMLElement | null>, enabled = true) {
    useSwipeNavigation(root, enabled, 'tabs');
}

/** Shared direction locking for tab content and paged calendars; invokes visible controls. */
export function useSwipeNavigation(root: RefObject<HTMLElement | null>, enabled = true, mode: 'tabs' | 'pages' = 'pages') {
    const active = useRouteActive();
    const settleBeforePaint = useRef<(() => void) | null>(null);
    const tabs = (host: HTMLElement) => {
        if (mode === 'pages') return Array.from(host.querySelectorAll<HTMLButtonElement>('[data-swipe-page]'));
        const bar = host.querySelector<HTMLElement>(':scope > .ui-header-tabs, :scope > [data-swipe-tab-bar]');
        return bar ? Array.from(bar.querySelectorAll<HTMLButtonElement>('button[role="tab"], button[aria-pressed]'))
            .filter(button => !button.disabled && button.getAttribute('aria-disabled') !== 'true') : [];
    };
    useLayoutEffect(() => {
        const host = root.current;
        if (host) host.toggleAttribute('data-swipe-tabs-scope', enabled && active && tabs(host).length > 1);
        settleBeforePaint.current?.();
    });
    useEffect(() => {
        const host = root.current;
        if (!host || !enabled || !active) return;
        const mobile = window.matchMedia('(max-width: 767px)');
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
        const selected = (button: HTMLElement) => button.getAttribute('aria-selected') === 'true' || button.getAttribute('aria-pressed') === 'true';
        const editable = 'input,textarea,select,[contenteditable]:not([contenteditable="false"]),[role="slider"]';
        const excluded = editable + ',[data-swipe-ignore],[data-group-drag-handle],[data-sortable-group],[draggable="true"],img,canvas,video,audio,[role="menu"],[data-anchored-popup],[data-list-toolbar],.ui-header-tabs,[data-swipe-tab-bar]';
        let gesture: {id:number;x:number;y:number;dx:number;time:number;axis:'pending'|'horizontal';width:number;samples:{x:number;time:number}[];buttons:HTMLButtonElement[];index:number;content:HTMLElement|null;transform:string} | null = null;
        let animation: Animation | undefined;
        let outgoing: {node:HTMLElement;animation?:Animation} | undefined;
        const clearOutgoing = () => { outgoing?.animation?.cancel(); outgoing?.node.remove(); outgoing = undefined; };
        let frame = 0;
        let moveFrame = 0;
        let promoted: {node:HTMLElement;willChange:string} | null = null;
        const releaseLayer = () => {
            if (promoted) promoted.node.style.willChange = promoted.willChange;
            promoted = null;
        };
        const promote = (node:HTMLElement) => {
            if (promoted?.node === node) return;
            releaseLayer(); promoted = {node,willChange:node.style.willChange}; node.style.willChange = 'transform';
        };
        const stopMotion = () => { animation?.cancel(); animation = undefined; releaseLayer(); clearOutgoing(); };
        const animate = (node:HTMLElement | null, from:string, to:string, duration:number) => {
            if (!node?.animate || reduced.matches) return;
            stopMotion(); promote(node);
            const running = node.animate([{transform:from},{transform:to}],{duration,easing:'cubic-bezier(.2,.8,.2,1)'});
            animation = running;
            running.finished.then(() => {if(animation === running) {animation=undefined;releaseLayer();}}, () => {});
        };
        let suppressClickUntil = 0;
        const reset = () => {
            cancelAnimationFrame(moveFrame); moveFrame = 0;
            if (gesture?.content) gesture.content.style.transform = gesture.transform;
            gesture = null;
            if (!animation) releaseLayer();
        };
        const start = (event: TouchEvent) => {
            reset(); cancelAnimationFrame(frame); settleBeforePaint.current = null; stopMotion();
            if (!mobile.matches || event.touches.length !== 1 || !host.hasAttribute('data-swipe-tabs-scope')) return;
            const target = event.target instanceof Element ? event.target : null;
            const touch = event.touches[0];
            if (!target || target.closest('[data-swipe-tabs-scope]') !== host || target.closest(excluded) ||
                target.closest('[data-drawer-id]') !== host.closest('[data-drawer-id]') ||
                document.activeElement?.matches(editable) || window.getSelection()?.toString() ||
                touch.clientX < 24 || touch.clientX > window.innerWidth - 24) return;
            // Horizontal carousels, tab strips and tables keep ownership of their gestures.
            for (let node: Element | null = target; node && node !== host; node = node.parentElement) {
                const style = getComputedStyle(node);
                if (style.touchAction === 'none' || (node.scrollWidth > node.clientWidth + 2 && /auto|scroll/.test(style.overflowX))) return;
            }
            const buttons = tabs(host);
            const index = mode === 'pages' ? 0 : buttons.findIndex(selected);
            if (buttons.length < 2 || index < 0) return;
            const content = host.querySelector<HTMLElement>(':scope > .swipe-tabs-content:not([hidden])');
            const width = host.clientWidth || window.innerWidth;
            const time = performance.now();
            gesture = {width,samples:[{x:touch.clientX,time}],id:touch.identifier,x:touch.clientX,y:touch.clientY,dx:0,time,axis:'pending',buttons,index,content,transform:content?.style.transform ?? ''};
        };
        const move = (event: TouchEvent) => {
            if (!gesture) return;
            if (!mobile.matches || event.touches.length !== 1) { reset(); return; }
            const touch = Array.from(event.touches).find(item => item.identifier === gesture!.id);
            if (!touch) { reset(); return; }
            const dx = touch.clientX - gesture.x, dy = touch.clientY - gesture.y;
            if (gesture.axis === 'pending') {
                if (Math.max(Math.abs(dx),Math.abs(dy)) < 10) return;
                if (Math.abs(dx) < Math.abs(dy) * 1.4) { reset(); return; }
                const next = mode === 'pages' ? (dx < 0 ? 1 : 0) : gesture.index + (dx < 0 ? 1 : -1);
                if (!gesture.buttons[next]) { reset(); return; }
                gesture.axis = 'horizontal';
                if (gesture.content && !reduced.matches) promote(gesture.content);
            }
            if (!event.cancelable) { reset(); return; }
            event.preventDefault();
            gesture.dx = dx;
            const time = performance.now();
            gesture.samples = [...gesture.samples.filter(sample => time - sample.time <= 100), {x:touch.clientX,time}];
            if (gesture.content && !reduced.matches && !moveFrame) {
                moveFrame = requestAnimationFrame(() => {
                    moveFrame = 0;
                    if (gesture?.content) gesture.content.style.transform = `translate3d(${mode === 'tabs' ? gesture.dx : Math.max(-96,Math.min(96,gesture.dx))}px,0,0)`;
                });
            }
        };
        const end = (event: TouchEvent) => {
            if (!gesture) return;
            const current = gesture;
            const touch = Array.from(event.changedTouches).find(item => item.identifier === current.id);
            const dx = touch ? touch.clientX - current.x : current.dx;
            const time = performance.now();
            const elapsed = time - current.time;
            const sample = current.samples.find(item => time - item.time <= 100);
            const velocity = sample ? ((touch?.clientX ?? current.x + current.dx) - sample.x) / Math.max(1, time - sample.time) : 0;
            const threshold = Math.min(96, Math.max(48, current.width * .18));
            const next = current.buttons[mode === 'pages' ? (dx < 0 ? 1 : 0) : current.index + (dx < 0 ? 1 : -1)];
            const commit = mobile.matches && current.axis === 'horizontal' &&
                (mode === 'tabs'
                    ? (Math.abs(dx) >= threshold || (Math.abs(dx) >= 24 && Math.abs(velocity) > .45)) && !(Math.abs(velocity) > .45 && Math.sign(velocity) !== Math.sign(dx))
                    : elapsed < 900 && (Math.abs(dx) >= threshold || (Math.abs(dx) >= 40 && Math.abs(dx) / Math.max(1,elapsed) > .5))) &&
                next?.isConnected && !next.disabled && (mode === 'pages' || selected(current.buttons[current.index]));
            const transform = `translate3d(${mode === 'tabs' ? current.dx : Math.max(-96,Math.min(96,current.dx))}px,0,0)`;
            reset();
            if (current.axis !== 'horizontal') return;
            if (event.cancelable) event.preventDefault();
            suppressClickUntil = performance.now() + 500;
            if (commit) {
                // Keep inert outgoing pixels during the handoff, without mounting
                // another tab or issuing its data reads before selection.
                let exit: {frame:HTMLElement;content:HTMLElement} | undefined;
                if (mode === 'tabs' && !reduced.matches && current.content?.animate) {
                    const rect = current.content.getBoundingClientRect();
                    if (rect.width && rect.height) {
                        const copy = current.content.cloneNode(true) as HTMLElement;
                        copy.removeAttribute('id'); copy.removeAttribute('data-testid');
                        copy.querySelectorAll('[id],[data-testid]').forEach(node => {node.removeAttribute('id');node.removeAttribute('data-testid');});
                        const originalNodes = [current.content, ...Array.from(current.content.querySelectorAll<HTMLElement>('*'))];
                        const copyNodes = [copy, ...Array.from(copy.querySelectorAll<HTMLElement>('*'))];
                        const frame = document.createElement('div');
                        frame.setAttribute('aria-hidden','true'); frame.setAttribute('inert','');
                        Object.assign(frame.style,{position:'fixed',left:`${rect.left}px`,top:`${rect.top}px`,width:`${rect.width}px`,height:`${rect.height}px`,overflow:'hidden',pointerEvents:'none',zIndex:'100010'});
                        Object.assign(copy.style,{width:'100%',height:'100%',margin:'0',transform});
                        frame.appendChild(copy); document.body.appendChild(frame);
                        originalNodes.forEach((node,index) => {copyNodes[index].scrollTop=node.scrollTop;copyNodes[index].scrollLeft=node.scrollLeft;});
                        exit = {frame,content:copy};
                        outgoing = {node:frame};
                    }
                }
                // React runs this layout effect before painting the newly selected panel.
                // The RAF fallback covers guarded actions that do not render the owner.
                settleBeforePaint.current = () => {
                    settleBeforePaint.current = null; cancelAnimationFrame(frame);
                    // Transfer the pending snapshot to the committed transition.
                    if (exit && outgoing?.node === exit.frame) outgoing = undefined;
                    if (mode === 'tabs' && !selected(next)) {
                        exit?.frame.remove();
                        animate(current.content,transform,current.transform || 'none',180); return;
                    }
                    const bar = next.closest<HTMLElement>('.ui-header-tabs,[data-swipe-tab-bar]');
                    if (bar) {
                        const itemRect = next.getBoundingClientRect(), barRect = bar.getBoundingClientRect();
                        if (itemRect.left < barRect.left) bar.scrollLeft += itemRect.left - barRect.left;
                        else if (itemRect.right > barRect.right) bar.scrollLeft += itemRect.right - barRect.right;
                    }
                    const content = host.querySelector<HTMLElement>(':scope > .swipe-tabs-content:not([hidden])');
                    const distance = mode === 'tabs' ? current.width : 32;
                    const offset = mode === 'tabs' ? current.dx + (dx < 0 ? distance : -distance) : (dx < 0 ? distance : -distance);
                    const duration = mode === 'tabs' ? 220 : 160;
                    animate(content,`translate3d(${offset}px,0,0)`,content?.style.transform || 'none',duration);
                    if (exit) {
                        const frame = exit.frame;
                        const motion = exit.content.animate([{transform},{transform:`translate3d(${dx < 0 ? -distance : distance}px,0,0)`}],{duration,easing:'cubic-bezier(.2,.8,.2,1)',fill:'forwards'});
                        outgoing = {node:frame,animation:motion};
                        const finish = () => {frame.remove();if(outgoing?.node===frame)outgoing=undefined;};
                        motion.finished.then(finish,finish);
                    }
                };
                next.click();
                if (settleBeforePaint.current) frame = requestAnimationFrame(() => settleBeforePaint.current?.());
            } else {
                animate(current.content,transform,current.transform || 'none',180);
            }
            if (!animation) releaseLayer();
        };
        const click = (event: MouseEvent) => {
            if (event.detail > 0 && performance.now() < suppressClickUntil) { event.preventDefault(); event.stopPropagation(); }
        };
        const cancel = () => { reset(); stopMotion(); cancelAnimationFrame(frame); settleBeforePaint.current = null; };
        host.addEventListener('touchstart',start,{passive:true});
        host.addEventListener('touchmove',move,{passive:false});
        host.addEventListener('touchend',end,{passive:false});
        host.addEventListener('touchcancel',cancel);
        host.addEventListener('click',click,true);
        mobile.addEventListener('change',cancel);
        return () => {
            cancel(); host.removeAttribute('data-swipe-tabs-scope');
            host.removeEventListener('touchstart',start);host.removeEventListener('touchmove',move);host.removeEventListener('touchend',end);
            host.removeEventListener('touchcancel',cancel);host.removeEventListener('click',click,true);mobile.removeEventListener('change',cancel);
        };
    }, [root, enabled, active, mode]);
}
